import { makeAutoObservable, runInAction } from "mobx";
import { IS2ELayoutItem } from "../../models/User";
import { IChangeable } from "../types";

export interface INewBackgroundImageData {
  base64_content: string;
  type: string;
}

interface IS2ELayoutItemChanges extends Partial<IS2ELayoutItem> {
  _new_image?: {
      base64_content: string;
      type: string;
  };
  _delete_image?: boolean;
}

export class S2ELayoutItemStore implements IChangeable<IS2ELayoutItem> {
  private _changes: IS2ELayoutItemChanges = {};
  private readonly _model: IS2ELayoutItem;

  private _image_dimensions?: string;

  constructor(data: IS2ELayoutItem) {
      makeAutoObservable(this, {}, { autoBind: true });

      this._model = data;

      this._setImageOriginalDimensions();
  }

  public get image(): string | undefined {
      if (this._changes._delete_image) {
          return undefined;
      }
      if (this._changes._new_image) {
          return this._getNewImageBase64Str(this._changes._new_image)
      } else {
          return this._model?.image;
      }
  }

  public get link(): string | undefined {
      return this._changes.link ?? this._model?.link;
  }

  public uploadImage(type: string, base64_content: string, callback: () => void) {
      this._changes._new_image = {
          base64_content,
          type
      };
      delete this._changes._delete_image;
      this._setImageOriginalDimensions(callback);
  }

  public setLink(link: string) {
      this._changes.link = link;
  }

  discardChanges(): void {
      this._changes = {} as IS2ELayoutItem;
  }

  getUpdatedModel(): IS2ELayoutItem {
      return { ...this._model, ...this._changes };
  }

  hasChanges(): boolean {
      return !!Object.keys(this._changes).length;
  }

  public get imageDimensions() {
      return this._image_dimensions;
  }

  public deleteImage() {
      this._changes._delete_image = !!this._model?.image;
      delete this._changes._new_image;
  }

  private _getNewImageBase64Str(data?: INewBackgroundImageData): string | undefined {
      if (data) {
          return `data:${data.type};base64,${data.base64_content}`;
      }
      return undefined;
  }

  private _setImageOriginalDimensions(callback?: () => void) {
      if (!this.image) return;

      this._loadBackgroundImage(this.image, (img: HTMLImageElement) => {
          this._image_dimensions = img.width + 'x' + img.height;
          callback && callback();
      })
  }

  private _loadBackgroundImage(src: string, callback: (image: HTMLImageElement) => void) {
      const img = new Image();
      img.src = src;
      img.onerror = (err) => {
          console.error(err);
      };
      img.onload = () => {
          runInAction(() => callback(img));
      };
  }
}